import { Adapter, AbstractAdapter } from "@app/shared/models/adapter.model";
import { CrudModes } from "@app/shared/components/abstracts/base-component";
import { Field } from "@app/shared/models/form-field.model";
import { Pagination, Sortable, Filtering } from "@app/shared/models/server-pagination.model";
import { FileDownloadStatus } from "@app/shared/models/file.model";

/**
 * Enumerator: viewStates
 *
 * View component states
 *
 * Value to control which component is currently being displayed
 */
export enum viewStates {
  viewJobs
}

export enum JobReadStatus {
  markAsUnread = "0",
  markAsRead = "1"
}

/**
 * Class: MyReportsParms
 *
 * Parameters used for MyReports form
 */
export class MyReportsParms {
  constructor(
    public id: string,
    public mode: CrudModes,
    public parent: string
  ) {}
}

/**
 * Interface: Jobs
 *
 * Implements data structure for Jobs
 */
export interface MyJobs {
  pagination: Pagination;
  sortable: Sortable;
  filtering: Filtering;
  startDate: Field;
  endDate: Field;
  userId: Field;
  userJobId: Field;
  description: Field;
  status: Field;
  jobsList: MyJobsList[];
}

/**
 * my report slist
 */
export interface MyJobsList {
  jobId: string;
  jobName: string;
  jobDescription: string;
  jobUser: string;
  jobEnvId: string;
  jobStatus: string;
  jobServerName: string;
  jobSubmitted: string | Date;
  hasMessages: boolean;
  messageCount: number;
  canHold: boolean;
  canRelease: boolean;
  canRetry: boolean;
  canPurge: boolean;
  documentCount: number;
  isSelected: boolean;
  isJobNew: boolean;
  canEdit: boolean;
  canDelete: boolean;

  _isJobNew: string;
  _jobStatus: string;
}

/**
 * Interface: JobCount
 */
export interface JobCount {
  totalUnread: number;
  totalJobs: number;
  totalRunning: number;
  totalCompleted: number;
}

export interface JobRecord {
  jobId: string;
  jobDescription: Field;
  jobUser: Field;
}

/**
 * Interface: MyReports
 *
 * Implements data structure for MyReports
 */
export interface MyReports {
  userJobId: string;
  docsList: DocumentsList[];
}

/**
 * Implements a data structure for selected job id's
 */
export interface MySelectedJobs {
  jobItems: string[];
}

/**
 * Array that is used for table source as well as in MyReports
 */
export interface DocumentsList {
  jobId: string;
  jobSequence: string;
  jobDocName: string;
  jobDocPath: string;
  jobDocDescription: string;
  canConvertToText: boolean;
  isSelected: boolean;
  downloadFileStatus: FileDownloadStatus;
}

/**
 * Implements data structure for selected documents
 */
export interface MySelectedDocuments {
  userJobId: string;
  docList: string[];
}

/**
 * Implements data structure for MyReportsMessages
 */
export interface MyMessages {
  userJobId: string;
  messageList: MessageList[];
}

/**
 *  Array that is used for table source as well as in MyMessages
 */
export interface MessageList {
  timeStamp: string | Date;
  message: string;
}

export interface ReportAsTextFile {
  jobId: string;
  jobSequence: string;
  textFile: string;
}

export interface MyReportsViewDocument {
  file: string;
  url: string;
}

/**
 * Class: MyJobsAdapter
 *
 * Implements a data adapter for my reports jobs
 */
export class MyReportsJobsRequestAdapter extends AbstractAdapter<MyJobs> {
  adapt(rawData: MyJobs): MyJobs {
    rawData.startDate.value = this.dateService.isValidDate(rawData.startDate.value as Date)
      ? this.dateService.dateToTimeStamp(rawData.startDate.value as Date)
      : rawData.startDate.value;
    rawData.endDate.value = this.dateService.isValidDate(rawData.endDate.value as Date)
      ? this.dateService.dateToTimeStamp(rawData.endDate.value as Date)
      : rawData.endDate.value;
    rawData.jobsList = [];

    if (rawData.userId.value === "*all") {
      rawData.userId.value = "";
    }

    if (rawData.sortable.sortColumn === "_isJobNew" || rawData.sortable.sortColumn === "_jobStatus") {
      rawData.sortable.sortColumn = rawData.sortable.sortColumn.replace(/_/g, "");
    }

    return rawData;
  }
}

/**
 * Class: MyJobsAdapter
 *
 * Implements a data adapter for my reports jobs
 */
export class MyReportsJobsResponseAdapter extends AbstractAdapter<MyJobs> {
  adapt(rawData: MyJobs): MyJobs {
    rawData.startDate.value = rawData.startDate.value ? (this.dateService.timeStampToDate(rawData.startDate.value as string) as Date) : "";
    rawData.endDate.value = rawData.endDate.value ? (this.dateService.timeStampToDate(rawData.endDate.value as string) as Date) : "";

    if (rawData.sortable.sortColumn === "isJobNew" || rawData.sortable.sortColumn === "jobStatus") {
      rawData.sortable.sortColumn = "_" + rawData.sortable.sortColumn;
    }

    rawData?.jobsList.map((e) => {
      e.jobSubmitted = this.dateService.timeStampToDate(e.jobSubmitted as string);
      e._isJobNew = this.translate(e.isJobNew ? "MY_REPORTS.LBL_UNREAD" : "MY_REPORTS.LBL_READ");
      e._jobStatus = this.translate(e.jobStatus);

      e.canEdit = this.authService.isAdmin
        ? true
        : e.jobUser.toUpperCase() === this.authService.credentials.username.toUpperCase() || e.jobUser.toUpperCase() === "UPLOAD";
      e.canDelete = this.authService.isAdmin
        ? true
        : e.jobUser.toUpperCase() === this.authService.credentials.username.toUpperCase() || e.jobUser.toUpperCase() === "UPLOAD";

      e.isSelected = false;
      return e;
    });

    return rawData;
  }
}

/**
 * Class: MyReportsAdapter
 *
 * Implements a data adapter for my reports
 */
export class MyReportsAdapter implements Adapter<MyReports> {
  adapt(rawData: any): MyReports {
    const reportList: DocumentsList[] = (rawData as MyReports).docsList.map((e) => {
      e.isSelected = false;
      e.downloadFileStatus = { status: "", loaded: 0 };
      return e;
    });
    return { userJobId: rawData.userJobId, docsList: reportList };
  }
}

/**
 * Class: MyReportsMessagesAdapter
 *
 * Implements a data adapter for my reports
 */
export class MyReportsMessagesAdapter extends AbstractAdapter<MyMessages> {
  adapt(rawData: any): MyMessages {
    const messageList: MessageList[] = (rawData as MyMessages).messageList.map((e) => {
      e.message = this.sentenceCase(e.message);
      e.timeStamp = this.dateService.timeStampToDate(e.timeStamp as string);
      return e;
    });
    return { userJobId: rawData.userId, messageList: messageList };
  }
}

/**
 * Class: JobCountAdapter
 *
 * Implements a data adapter for job counts
 */
export class JobCountAdapter implements Adapter<JobCount> {
  adapt(rawData: any): JobCount {
    return rawData;
  }
}

export class ReportAsTextFileAdapter implements Adapter<ReportAsTextFile> {
  adapt(rawData: any): ReportAsTextFile {
    return rawData;
  }
}

export class MyReportsViewDocumentAdapter implements Adapter<MyReportsViewDocument> {
  adapt(rawData: any): MyReportsViewDocument {
    return rawData;
  }
}
